import {
    authenticate as authenticateRequest,
    AuthenticateParams,
    AuthenticateResponse
} from '../../api/authorization/authenticate';
import { validate as validateRequest, ValidateResponse } from '../../api/authorization/validate';
import Routes from '../../constants/routes';
import { declineAgb, getUser, setLoaded } from '../../reducers/user';
import store from '../../store';
import { storageKeys } from '../../utils/browser-storage/storage-keys';
import { clearAuthTokenAndUpdateUrl, clearAuthUrl } from '../../utils/clear-auth-token-and-update-url';
import isUITest from '../../utils/is-ui-test';
import session from '../../utils/session';
import serializeQueryParams from '../../utils/url/serialize-query-params';
import qualityEvents from '../quality-events/quality-events';
import logout from './logout';

type DeferredState = {
    isAgbAccepted: boolean;
    validation?: boolean | null;
    isPending?: boolean;
    pendingPromise?: Promise<ValidateResponse | void>;
    credentials?: Promise<AuthenticateResponse>;
};

type LoginGrantType = 'password' | 'apple' | 'google' | 'facebook' | 'microsoft' | 'clientCredentials';

type PasswordParams = {
    username?: string;
    password?: string;
};

const CLIENT_ID = 'oCGxyq24sKTcnvsr';
const LOGOUT_DELAY = 1000;

const checkWrongAccountLogin = (response: ValidateResponse = {}) => {
    // This condition is experimental – here we track wrongAccount logins.
    // Sometimes users find out that they are logged into wrong accounts
    // And to fix it we compare ids from /authenticate (stored in localStorage) and /validate requests
    const { oauth_user_id: oauthUserId } = response;
    const storedOauthUserId = window.storage.getItem(storageKeys.user.oauthUserId);

    const isWrongAccountLogin =
        oauthUserId &&
        storedOauthUserId &&
        session.isLoggedIn() &&
        !session.impersonated() &&
        window.location.href.indexOf(Routes.SIGN_UP_CONFIRM.path) < 0 &&
        Number(oauthUserId) !== Number(storedOauthUserId);

    if (isWrongAccountLogin) {
        if (!isUITest()) {
            qualityEvents.authentication.wrongAccountLoginByValidate(Number(oauthUserId), Number(storedOauthUserId));
        }

        setTimeout(logout, LOGOUT_DELAY); // time to send QE
    }
};

const oauth = (() => {
    const deferred: DeferredState = { isAgbAccepted: true };

    return {
        apple(params: AuthenticateParams): Promise<AuthenticateResponse> {
            return new Promise((resolve, reject) => {
                this.authenticate({
                    client_id: CLIENT_ID,
                    grant_type: 'apple_id',
                    platform: 'web',
                    ...params
                }).then(
                    (response) => {
                        session.setAccessToken(response.access_token);
                        session.clearError();
                        session.setLoggedIn(1);
                        resolve(response);
                    },
                    (error) => reject(error)
                );
            });
        },
        authenticate(params: AuthenticateParams, headers?: Record<string, string>): Promise<AuthenticateResponse> {
            return authenticateRequest(params, headers);
        },
        changeDeferred<K extends keyof DeferredState>(key: K, value: DeferredState[K]): void {
            deferred[key] = value;
        },
        clientCredentials(): Promise<AuthenticateResponse> {
            if (deferred.credentials) {
                return deferred.credentials;
            }

            deferred.credentials = new Promise((resolve, reject) => {
                this.authenticate({
                    client_id: CLIENT_ID,
                    grant_type: 'client_credentials',
                    scopes: ['public']
                }).then(
                    (response) => {
                        session.setLoggedIn(0);
                        resolve(response);
                    },
                    (error) => reject(error)
                );
            });

            return deferred.credentials;
        },
        facebook(token: string): Promise<AuthenticateResponse> {
            return new Promise((resolve, reject) => {
                this.authenticate({
                    client_id: CLIENT_ID,
                    facebook_access_token: token,
                    grant_type: 'facebook'
                }).then(
                    (response) => {
                        session.setAccessToken(response.access_token);
                        session.clearError();
                        session.setLoggedIn(1);
                        resolve(response);
                    },
                    (error) => reject(error)
                );
            });
        },
        google(params: AuthenticateParams): Promise<AuthenticateResponse> {
            return new Promise((resolve, reject) => {
                this.authenticate({
                    client_id: CLIENT_ID,
                    grant_type: 'google',
                    platform: 'web',
                    ...params
                }).then(
                    (response) => {
                        session.setAccessToken(response.access_token);
                        session.clearError();
                        session.setLoggedIn(1);
                        resolve(response);
                    },
                    (error) => reject(error)
                );
            });
        },

        isAgbAccepted(): boolean {
            return deferred.isAgbAccepted;
        },

        login(type: LoginGrantType = 'password', params?: unknown): Promise<AuthenticateResponse> {
            deferred.validation = null;

            return new Promise((resolve, reject) => {
                (this[type] as (p?: unknown) => Promise<AuthenticateResponse>)(params).then(
                    (response) => {
                        const queryParams = serializeQueryParams();

                        if (queryParams.clear_auth !== undefined) {
                            clearAuthTokenAndUpdateUrl();
                        } else {
                            session.setAccessToken(response.access_token);
                        }

                        session.clearError();
                        resolve(response);
                    },
                    (error) => reject(error)
                );
            });
        },

        microsoft(params: AuthenticateParams): Promise<AuthenticateResponse> {
            return new Promise((resolve, reject) => {
                this.authenticate({
                    client_id: CLIENT_ID,
                    grant_type: 'microsoft',
                    ...params
                }).then(
                    (response) => {
                        session.setAccessToken(response.access_token);
                        session.clearError();
                        session.setLoggedIn(1);
                        resolve(response);
                    },
                    (error) => reject(error)
                );
            });
        },
        password(params: PasswordParams = {}): Promise<AuthenticateResponse> {
            const { username, password } = params;

            return new Promise((resolve, reject) => {
                this.authenticate({
                    client_id: CLIENT_ID,
                    grant_type: 'password',
                    password,
                    username
                }).then(
                    (response) => {
                        if (response?.oauth_user_id) {
                            window.storage.setItem(storageKeys.user.oauthUserId, response.oauth_user_id);
                        }

                        session.setLoggedIn(1);
                        window.storage.setItem(storageKeys.user.impersonated, 0);
                        resolve(response);
                    },
                    (error) => reject(error)
                );
            });
        },
        validate(): Promise<ValidateResponse | void> {
            if (deferred.validation) {
                return Promise.resolve();
            }

            if (deferred.isPending && deferred.pendingPromise) {
                return deferred.pendingPromise;
            }

            deferred.isPending = true;

            deferred.pendingPromise = new Promise<ValidateResponse | void>((resolve, reject) => {
                validateRequest()
                    .then(
                        (response) => {
                            checkWrongAccountLogin(response);

                            if (response) {
                                const queryParams = serializeQueryParams();

                                if (queryParams.clear_auth !== undefined) {
                                    clearAuthTokenAndUpdateUrl();
                                } else {
                                    window.storage.setItem(storageKeys.auth.accessToken, response.oauth_access_token);
                                }
                            }

                            if (
                                response?.oauth_user_id &&
                                window.location.href.indexOf(Routes.SIGN_UP_CONFIRM.path) < 0
                            ) {
                                session.setLoggedIn(1);
                                window.storage.setItem(storageKeys.user.userId, response.oauth_user_id);
                                getUser()(store.dispatch, store.getState as () => RootState);
                            } else {
                                session.setLoggedIn(0);
                                setLoaded()(store.dispatch);
                            }

                            deferred.validation = true;
                            deferred.isAgbAccepted = true;
                            deferred.isPending = false;
                            resolve(response);
                        },
                        (error: { data?: { reason?: string } }) => {
                            if (error?.data?.reason === 'terms_not_accepted') {
                                deferred.isAgbAccepted = false;
                                deferred.validation = false;
                                declineAgb()(store.dispatch);
                                deferred.isPending = false;

                                return reject(error);
                            }

                            oauth.login('clientCredentials').then(
                                () => {
                                    session.setLoggedIn(0);
                                    setLoaded()(store.dispatch);
                                    deferred.validation = false;
                                    deferred.isAgbAccepted = true;
                                    deferred.isPending = false;
                                    resolve();
                                },
                                () => {
                                    session.setLoggedIn(0);
                                    session.setError('authError');
                                    deferred.isPending = false;
                                }
                            );
                        }
                    )
                    .finally(clearAuthUrl);
            });

            return deferred.pendingPromise;
        }
    };
})();

export default oauth;
